import type { FieldValues, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { emailValidationRules, FormInput } from "@components/forms";

import { CHECKOUT_INPUT_CLASS } from "./checkoutForm";

// Multi-step payment-first (U2). The card-path charge email, rendered as the
// LAST field of the card form — directly under the Drop-in's Postal Code via
// BraintreeCheckout's cardEmailSlot (OMG-2102, per mock) — only when Card is
// the selected method. A single email field — no confirm field (the
// confirm-email match moved to the post-charge Create-account screen, KTD8)
// and no login link/TOS (the method-select screen carries the header Login
// and the OMG-1929 assent line). Kept self-contained so it deletes cleanly
// when the A/B test concludes.
export const VariantCardEmail = ({
  register,
  variantEmailError,
  onClearErrors,
  onEngage,
}: {
  register: UseFormRegister<FieldValues>;
  variantEmailError: boolean;
  onClearErrors: () => void;
  // OMG-2313: first focus of this field is a card-form micro-funnel step. The
  // parent owns the single-fire guard and the analytics call.
  onEngage?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    // No top margin: the Drop-in's Postal Code field-group above reserves an
    // 18px validation-error line below its input, which IS the form's
    // between-rows rhythm — adding margin here would read as a wider gap than
    // the Drop-in's own fields (OMG-2102: "as close as possible to regular
    // spacing"; value subject to design sign-off).
    //
    // `relative` + an absolutely-positioned error (below) keep the validation
    // message OUT of the vertical flow, so the error line itself never inflates
    // the gap while a buyer types. variantEmailError is live per-keystroke, so an
    // in-flow error line would push Buy Now toward the fold for the whole time
    // (OMG-2174).
    //
    // OMG-2389: that alone printed the error ON TOP of the assent line. Out of
    // flow, the error box occupies 4px to 28px below this wrapper while
    // AssentLine's box starts at 16px (its mt-[16px]), so measured they overlapped
    // by 12px and their glyphs by 4px — across the line carrying the PayPal
    // Privacy Statement and Terms links, which AssentLine's own translationBroken
    // guard exists to protect. So the gap is NO LONGER constant in every state:
    // while an error shows, the wrapper reserves 32px, which puts AssentLine's box
    // at 32px and clears the error's 28px with 4px to spare, mirroring the error's
    // own mt-[4px] above it. Glyphs then clear by 12px.
    //
    // 32px, NOT 16px, because these margins COLLAPSE. AssentLine is the next
    // sibling and carries mt-[16px], so an adjacent bottom margin here collapses
    // to max(mine, 16) rather than summing: mb-[16px] resolves to 16px total and
    // moves nothing at all. Measured, assentTop simply equals this value once it
    // is >= 16, so the number here IS the resulting offset. mb-[28px] would make
    // the boxes touch exactly; 32 is the first value with real clearance.
    // That collapse assumes the ancestor chain stays block-level: it propagates
    // up through the unclassed <form> and onInput div (both padding/border-free)
    // to .billing-container (BraintreeCheckout.tsx), which has no CSS rule
    // anywhere in the repo and is therefore a default block box. If that section
    // ever gains `flex flex-col` or `grid`, margins stop collapsing and this
    // becomes 16+32=48px. That is more clearance, not less, so the failure
    // direction is safe, but the number here would no longer be the offset.
    // Padding is not an option either: the error is positioned `top-full`, which
    // resolves against the padding box, so padding-bottom pushes the error down
    // with it and the overlap survives.
    //
    // Reserved on the WRAPPER, not the error: the error is out of flow, so a
    // margin on it would move nothing. Reserved only while the error shows, so the
    // no-error render is byte-identical and the running A/B baseline does not
    // move — that constraint outranks holding the gap perfectly constant, which is
    // the tradeoff Jason picked on 2026-08-14. Net effect: content below shifts
    // down 16px on the error transition (16px to 32px).
    <div
      className={`relative w-full max-w-[500px]${
        variantEmailError ? " mb-[32px]" : ""
      }`}
    >
      <FormInput
        id="variant-card-email"
        inputClassName={CHECKOUT_INPUT_CLASS}
        labelClassName="tracking-normal cursor-pointer"
        errorClassName="hidden"
        inputErrorClassName="!border !border-solid !border-error focus:!border-error"
        showErrorIcon
        type="email"
        autoComplete="email"
        inputMode="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        // Mask the email from session recording (Sentry/Clarity) — the
        // documented masking launch-gate for the U2/U5 email fields
        // (paymentFirst.ts). VariantSetPassword carries the same attributes.
        data-sentry-mask
        data-clarity-mask
        // No autoFocus / focus management (OMG-2102): as the form's last
        // field, the Drop-in's own card-number autofocus is the correct
        // landing spot; the OMG-2097 focus overrides were retired with the
        // email-first layout.
        required
        className="w-full flex flex-col gap-[4px]"
        register={() =>
          register("email", emailValidationRules(t("js.checkout.emailInvalid")))
        }
        label={t("checkoutPage.email")}
        error={variantEmailError ? {} : undefined}
        // Passes through FormInput's InputHTMLAttributes to the input; RHF's
        // register() returns no onFocus, so it can't clobber this.
        onFocus={onEngage}
        onChange={() => onClearErrors()}
        placeholder={t("checkoutPage.email")}
      />
      {/* Absolutely positioned so the message overlays the space below the
          input rather than adding layout height (OMG-2174). role="alert"
          announces it on appearance since the node mounts only on error. */}
      {variantEmailError && (
        <p
          role="alert"
          className="absolute left-0 top-full mt-[4px] px-16px sm:px-0 leading-[24px] font-mabry-pro-medium text-sm font-medium text-error"
        >
          {t("js.checkout.emailInvalid")}
        </p>
      )}
    </div>
  );
};
